import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '~/components/common/info-tooltip/info-tooltip';
import { Card, CardContent } from '~/components/ui/card';
import type { CoverageStat } from '~/features/catalog-operations/types/catalog-operations.types';
import { cn } from '~/utils/cn';

const SUB_TONE: Record<NonNullable<CoverageStat['sub']>['tone'], string> = {
  positive: 'text-success',
  neutral: 'text-muted-foreground',
};

const COLS_CLASS: Record<2 | 3 | 4, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
};

interface Props {
  stats: CoverageStat[];
  columns?: 2 | 3 | 4;
}

/** Renders the Enrichment Coverage summary cards, each with an optional info tooltip on its label. */
export function CoverageStatsRow({ stats, columns = 4 }: Props) {
  const { t } = useTranslation('catalog-operations');

  return (
    <div
      data-testid="batch-detail-coverage-stats"
      className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', COLS_CLASS[columns])}
    >
      {stats.map((stat, index) => (
        <Card
          key={stat.label}
          data-testid={`batch-detail-coverage-stat-card-${String(index)}`}
          className="shadow-stat-card"
        >
          <CardContent className="space-y-2 p-5">
            <div className="flex items-center gap-1">
              <p
                className={cn(
                  'text-sm',
                  stat.isInProgress ? 'text-stage-active' : 'text-muted-foreground',
                  'font-medium',
                )}
              >
                {stat.label}
              </p>
              {stat.tooltip && (
                <InfoTooltip
                  content={stat.tooltip}
                  ariaLabel={t('coverageStatsRow.infoLabel', { label: stat.label })}
                  testId={`batch-detail-coverage-stat-card-${String(index)}-info-tooltip`}
                />
              )}
            </div>
            <p
              data-testid={`batch-detail-coverage-stat-value-${String(index)}`}
              className="text-2xl font-bold tracking-tight tabular-nums"
            >
              {stat.value}
            </p>
            {stat.sub && (
              <p className={cn('text-xs font-medium', SUB_TONE[stat.sub.tone])}>{stat.sub.text}</p>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
